import { useMemo, useContext } from 'react'
import { UserContext } from '../context/userContext'
import { Role, Application } from '../types/user'

/**
 * Base hook for checking user permissions
 * @param {Object} params - Permission parameters
 * @param {Array<string>} params.roles - Array of roles to check against
 * @param {Array<string>} params.applications - Array of applications to check against
 * @returns {boolean} true if user has required permissions
 */
export function useUserPermissions({ roles = [], applications = [] } = {}) {
    const { state } = useContext(UserContext)
    const user = state.user

    return useMemo(() => {
        if (!user) {
            return false
        }

        if (roles.length === 0) {
            return false
        }

        const hasRole = roles.some((role) => user.roles?.includes(role))

        // If no applications specified, only check roles
        if (applications.length === 0) {
            return hasRole
        }

        // Check both roles and applications
        const hasApplication = applications.some((app) => user.applications?.includes(app))
        return hasRole && hasApplication
    }, [user, roles, applications])
}

/**
 * Hook to check if current user has Admin role
 * @returns {boolean} true if user is an admin
 */
export const useIsAdmin = () => {
    return useUserPermissions({ roles: [Role.Admin] })
}

/**
 * Hook to check if current user has User or Admin role
 * @returns {boolean} true if user has basic user access
 */
export const useIsUser = () => {
    return useUserPermissions({ roles: [Role.User, Role.Admin] })
}

/**
 * Hook to check if current user can edit projects
 * @returns {boolean} true if user has User/Admin role and Project application permission
 */
export const useIsProjectEditor = () => {
    return useUserPermissions({
        roles: [Role.User, Role.Admin],
        applications: [Application.Project],
    })
}
